import {createCheckout, createTicketReservation} from '@wix/ambassador-events-v1-ticket-reservation/http'
import {
  CreateCheckoutResponse,
  CreateTicketReservationResponse,
} from '@wix/ambassador-events-v1-ticket-reservation/types'
import {getDownloadUrlPublic} from '@wix/ambassador-events-v2-ticket/http'
import {GetDownloadUrlPublicRequest, GetDownloadUrlPublicResponse} from '@wix/ambassador-events-v2-ticket/types'
import {getSeatingReservationSummary} from '@wix/ambassador-seating-v1-seating-reservation/http'
import {GetSeatingReservationSummaryResponse} from '@wix/ambassador-seating-v1-seating-reservation/types'
import {getActivityStats} from '@wix/ambassador-social-groups-v1-activity-stats/http'
import {GetActivityStatsResponse} from '@wix/ambassador-social-groups-v1-activity-stats/types'
import {listGroupsByEventId} from '@wix/ambassador-social-groups-v1-group-event/http'
import {getGroup} from '@wix/ambassador-social-groups-v2-group/http'
import {GetGroupResponse} from '@wix/ambassador-social-groups-v2-group/types'
import {UpdateSiteSettingsResponse} from '@wix/ambassador-wix-events-web/types'
import {EventStatus} from '@wix/events-types'
import {addQueryParams} from '@wix/panda-js-utils'
import {
  EventsDates,
  ExperimentNames,
  PageData,
  PlanList,
  TrimmedMember,
  WIX_EVENTS_FQDN,
  defaultLocale,
  draftEventPreviewUrlQueryParamName,
} from '@wix/wix-events-commons-statics'
import {ControllerFlowAPI, ControllerParams} from '@wix/yoshi-flow-editor'
import {GET_SITE_SETTINGS_OLD, UPDATE_SITE_SETTINGS_OLD} from '../../../../commons/actions/site-settings'
import {SiteSettingsSettings} from '../../../../commons/types/state'
import {Api as BaseApi} from '../../../../commons/utils/api'
import {GET_CHECKOUT_OPTIONS_OLD} from '../actions/checkout-options'
import {GET_EVENT_OLD, GET_MEMBERS_OLD} from '../actions/event'
import {GET_CONNECTED_GROUP_OLD, GET_GROUP_ACTIVITY_OLD} from '../actions/groups'
import {FETCH_CURRENT_MEMBER_OLD, PROMPT_LOGIN_OLD} from '../actions/members'
import {PLACE_ORDER_OLD, UPDATE_ORDER_OLD} from '../actions/placed-order'
import {GET_POLICIES_OLD} from '../actions/policies'
import {RESERVE_TICKETS_OLD} from '../actions/reservation'
import {
  DELETE_RSVP_OLD,
  GET_MEMBER_RSVP_OLD,
  SEND_RSVP_OLD,
  UPDATE_RSVP_OLD,
  UPDATE_RSVP_STATUS_OLD,
} from '../actions/rsvp'
import {RegFormData} from '../types/registration'
import {extractFormData} from './api-data-mapper'

export class Api extends BaseApi {
  constructor(controllerParams: ControllerParams) {
    super(controllerParams)

    this.registrar = {
      [GET_EVENT_OLD.NAME]: this.getEvent,
      [GET_MEMBERS_OLD.NAME]: this.getMembers,
      [GET_CHECKOUT_OPTIONS_OLD.NAME]: this.getCheckoutOptions,
      [RESERVE_TICKETS_OLD.NAME]: this.makeReservation,
      [GET_MEMBER_RSVP_OLD.NAME]: this.getMemberRsvp,
      [SEND_RSVP_OLD.NAME]: this.createRsvp,
      [DELETE_RSVP_OLD.NAME]: this.deleteRsvp,
      [UPDATE_RSVP_OLD.NAME]: this.updateRsvp,
      [PROMPT_LOGIN_OLD.NAME]: this.promptLogin,
      [UPDATE_ORDER_OLD.NAME]: this.updateOrder,
      [PLACE_ORDER_OLD.NAME]: this.placeOrder,
      [FETCH_CURRENT_MEMBER_OLD.NAME]: this.getCurrentMember,
      [UPDATE_RSVP_STATUS_OLD.NAME]: this.updateRsvpStatus,
      [GET_SITE_SETTINGS_OLD.NAME]: this.getSiteSettings,
      [UPDATE_SITE_SETTINGS_OLD.NAME]: this.updateSiteSettings,
      [GET_POLICIES_OLD.NAME]: this.getPolicies,
      [GET_CONNECTED_GROUP_OLD.NAME]: this.getGroup,
      [GET_GROUP_ACTIVITY_OLD.NAME]: this.getGroupActivity,
    }
  }

  getData = ({
    slug,
    responsive = false,
    schedulePageInstalled,
    draftPreviewToken,
    flowAPI,
    newClassicEvents,
  }: GetDataArgs) => {
    const encodedSlug = encodeURIComponent(slug)

    if (slug.length > 130) {
      flowAPI.reportError(new Error('Slug is too long'))
      return {} as PageData
    }

    const url = addQueryParams(`/html/page-data/${encodedSlug}`, {
      compId: this.compId,
      locale: this.language,
      viewMode: this.viewMode,
      responsive: String(responsive),
      schedule: String(schedulePageInstalled),
      regional: this.locale,
      [draftEventPreviewUrlQueryParamName]: draftPreviewToken,
      tz: this.tz,
      defaultAlphabetical:
        flowAPI.controllerConfig?.wixCodeApi?.window?.viewMode !== 'Site' &&
        flowAPI.experiments.enabled(ExperimentNames.SausageIntegration),
      richContent: true,
      useRichContentFromEvent: true,
      newClassicEvents,
      noComponent: true,
    })

    return this.api.get<PageData>(url)
  }

  getOrder = async (
    eventId: string,
    orderNumber: string,
    token: string = '',
    locale: string = defaultLocale,
  ): Promise<{order: wix.events.ticketing.Order; calendarLinks: any; dates: any}> => {
    if (token) {
      return this.api.get(`/web/orders/by-token?token=${token}&locale=${locale}`)
    } else {
      return this.api.get(`/web/events/${eventId}/orders/${orderNumber}?locale=${locale}`)
    }
  }

  placeOrder = ({
    eventId,
    buyer,
    guests,
    couponCode,
    giftCardCode,
    memberId,
    planOrderId,
    benefitId,
    locale,
    paymentRedirectUrl,
  }: PlaceOrderArgs): Promise<{order: wix.events.ticketing.Order}> =>
    this.writeApi.post(`/web/events/${eventId}/checkout`, {
      [guests ? 'buyer' : 'data']: extractFormData(buyer),
      guests,
      reservationId: buyer.reservation,
      memberId,
      couponCode,
      giftCardCode,
      planOrderId,
      benefitId,
      locale,
      paymentRedirectUrl,
    })

  updateOrder = ({
    eventId,
    orderNumber,
    buyer,
    guests,
    locale,
    couponCode,
    giftCardCode,
    paidPlanBenefit,
    paymentDetailsId,
    paymentMethod,
  }: UpdateOrderParams): Promise<wix.events.ticketing.UpdateOrderResponse> =>
    this.writeApi.put(`/web/events/${eventId}/checkout/${orderNumber}`, {
      buyer,
      guests: guests?.length ? guests : undefined,
      locale,
      couponCode,
      giftCardCode,
      paidPlanBenefit,
      paymentDetailsId,
      paymentMethod,
    })

  getEvent = (eventId: string) => this.api.get(`/web/events/${eventId}/viewer`)

  getMembers = (eventId: string): Promise<{members: Member[]; totalGuests: TotalEventGuests}> =>
    this.api.get(`/web/events/${eventId}/members`)

  getInvoice = (
    eventId: string,
    reservationId: string,
    couponCode?: string,
    giftCardCode?: string,
    benefitId?: string,
    planOrderId?: string,
  ): Promise<wix.events.ticketing.GetInvoiceResponse> =>
    this.api.put(`/web/events/${eventId}/invoice`, {
      reservationId,
      couponCode,
      giftCardCode,
      benefitId,
      planOrderId,
    })

  getCheckoutOptions = async (): Promise<wix.events.ticketing.GetCheckoutOptionsResponse> => {
    try {
      const response = await this.api.get('/web/checkout-options')
      return response
    } catch (e) {
      return {
        premiumServices: true,
        paymentMethodConfigured: true,
        acceptCoupons: true,
        acceptGiftCards: true,
      }
    }
  }

  makeReservation = (
    eventId: string,
    ticketQuantities: wix.events.ticketing.TicketReservationQuantity[],
  ): Promise<{id: string; expires: string}> =>
    this.writeApi.post(`/web/events/${eventId}/reservations`, {
      ticketQuantities,
      userDateTime: new Date().toISOString(),
    })

  cancelReservation = (eventId: string, reservationId: string): Promise<{}> =>
    this.writeApi.delete(`/web/events/${eventId}/reservations/${reservationId}`)

  getMemberRsvp = async (eventId: string, memberId: string): Promise<wix.events.rsvp.Rsvp> =>
    this.api
      .get<{rsvp: wix.events.rsvp.Rsvp}>(`/web/events/${eventId}/member-rsvp?memberId=${memberId}`)
      .then(response => response.rsvp)

  getCurrentMember = (): Promise<{
    lastName: string
    firstName: string
    loginEmail: string
    id: string
  }> => this.api.get<{member: TrimmedMember}>(`/web/members/current`).then(response => response.member)

  createRsvp = (
    eventId: string,
    data: RegFormData,
    status: wix.events.rsvp.RsvpStatus,
    memberId?: string,
  ): Promise<wix.events.rsvp.CreateRsvpResponse> =>
    this.api.post(`/web/events/${eventId}/v2/rsvp`, {memberId, status, data})

  deleteRsvp = (eventId: string, rsvpId: string): Promise<void> =>
    this.api.delete(`/web/events/${eventId}/rsvp/${rsvpId}`)

  updateRsvp = (
    eventId: string,
    data: RegFormData,
    status: wix.events.rsvp.RsvpStatus,
    rsvpId: string,
  ): Promise<wix.events.rsvp.UpdateRsvpResponse> =>
    this.api.put(`/web/events/${eventId}/rsvp/${rsvpId}/form`, {data, status})

  updateRsvpStatus = (eventId: string, rsvpId: string, status: wix.events.rsvp.RsvpStatus): Promise<void> =>
    this.writeApi.put(`/web/events/${eventId}/rsvp/${rsvpId}/status`, {status})

  promptLogin = (lang: string) => {
    return new Promise(async (resolve, reject) => {
      try {
        await this.controller.wixCodeApi.user.promptLogin({
          mode: 'login',
          lang,
        })
        resolve(true)
      } catch (e) {
        if (typeof e !== 'string') {
          reject(e)
          return
        }
        resolve(false)
      }
    })
  }

  getPlanList = (eventId: string): Promise<{plans: PlanList}> =>
    this.api.get(`/web/plans?eventId=${eventId}&fixPaidPlans=true`)

  getSiteSettings = () => this.writeApi.get('/web/site-settings')

  updateSiteSettings = (settings: SiteSettingsSettings): Promise<UpdateSiteSettingsResponse> =>
    this.writeApi.put('/web/site-settings', {settings})

  getPolicies = (eventId: string): Promise<{policies: wix.events.Policy[]; policyAgreementToken?: string}> =>
    this.api.get(`/web/events/${eventId}/policies`)

  getGroup = async (eventId: string): Promise<GetGroupResponse> => {
    const {withErrorHandler} = this.errorHandler

    return withErrorHandler(
      async () => {
        const {
          data: {groupIds},
        } = await this.httpClient.request(listGroupsByEventId({eventId}))

        if (groupIds?.length) {
          return this.httpClient.request(getGroup({groupId: groupIds[0]})).then(r => r.data)
        }
      },
      {errorCodesMap: {}},
    ).catch(this.api.logError)
  }

  getGroupActivity = async (groupId: string): Promise<GetActivityStatsResponse> => {
    const {withErrorHandler} = this.errorHandler

    return withErrorHandler(() => this.httpClient.request(getActivityStats({groupId})).then(r => r.data), {
      errorCodesMap: {},
    }).catch(this.api.logError)
  }

  describeSeatingPlan = async (eventId: string): Promise<GetSeatingReservationSummaryResponse> => {
    const {withErrorHandler} = this.errorHandler

    return withErrorHandler(
      () =>
        this.httpClient
          .request(
            getSeatingReservationSummary({
              externalId: `${WIX_EVENTS_FQDN}${eventId}`,
            }),
          )
          .then(r => r.data),
      {errorCodesMap: {}},
    ).catch(this.api.logError)
  }

  getPapyrusTicketsDownloadUrl = async (args: GetDownloadUrlPublicRequest): Promise<GetDownloadUrlPublicResponse> => {
    const {withErrorHandler} = this.errorHandler

    return withErrorHandler(() => this.httpClient.request(getDownloadUrlPublic(args)).then(response => response.data), {
      errorCodesMap: {},
    }).catch(this.api.logError)
  }

  getStaticMapUrl = async (eventId: string, language: string): Promise<string> =>
    this.api.get<string>(addQueryParams(`/html/static-map/${eventId}`, {languageOverride: language}))

  getEventOccurrences = async (eventId: string, offset: number) =>
    this.api.get<GetOccurrencesResponse>(
      addQueryParams(`/web/events/${eventId}/occurrences`, {
        status: [EventStatus.SCHEDULED, EventStatus.STARTED],
        locale: this.locale,
        offset,
      }),
    )

  createTicketReservation = async (
    ticketsForReservation: {
      ticketDefinitionId: string
      quantity: number
      ticketInfo?: {
        guestPrice?: string
        pricingOptionId?: string
        seatId?: string
      }
    }[],
  ): Promise<CreateTicketReservationResponse> =>
    this.httpClient
      .request(
        createTicketReservation({
          ticketReservation: {
            tickets: ticketsForReservation,
          },
        }),
      )
      .then(response => response.data)

  createCheckout = async (ticketReservationId: string): Promise<CreateCheckoutResponse> =>
    this.httpClient.request(createCheckout({ticketReservationId})).then(response => response.data)
}

interface PlaceOrderArgs {
  eventId: string
  buyer: RegFormData
  guests: RegFormData[]
  couponCode: string
  giftCardCode: string
  memberId: string
  planOrderId: string
  benefitId: string
  locale: string
  paymentRedirectUrl?: string
}

interface GetDataArgs {
  slug: string
  responsive: boolean
  schedulePageInstalled: boolean
  draftPreviewToken: string
  flowAPI: ControllerFlowAPI
  newClassicEvents?: boolean
}

interface UpdateOrderParams {
  eventId: string
  orderNumber: string
  buyer: RegFormData
  guests: RegFormData[]
  locale: string
  couponCode: string
  giftCardCode: string
  paidPlanBenefit?: {benefitId: string; planOrderId: string}
  paymentDetailsId: string
  paymentMethod?: string
}

export interface GetOccurrencesResponse {
  events: wix.events.Event[]
  dates: {events: EventsDates}
  total: number
}
